import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Typography, 
  TextField, 
  Button, 
  Alert, 
  Box, 
  Paper,
  Divider,
  CircularProgress,
  Container
} from '@mui/material';
import { useAuth } from '../auth/Auth';
import "../profile/Profile.css";
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Business as BusinessIcon,
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Lock as LockIcon,
  Phone as PhoneIcon
} from '@mui/icons-material';

const UserProfile = () => {
  const location = useLocation();
  const { userId: paramUserId } = useParams();
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [token, setToken] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState('');

  const [contractor, setContractor] = useState(null);
  const [language, setLanguage] = useState('uk-UA');
  
  
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [isEditingUsername, setIsEditingUsername] = useState(false);
  const [isEditingToken, setIsEditingToken] = useState(false);
  const [isEditingPassword, setIsEditingPassword] = useState(false);
  
  const { user } = useAuth();
  
  
  const isOwnProfile = !paramUserId || paramUserId === user?.user_id?.toString();
  const targetUserId = paramUserId || user?.user_id;
  const isAdmin = user?.user_rule === '5' || user?.user_rule === 5;

  useEffect(() => {
    if (targetUserId) {
      fetchUserData();
      fetchContractorData();
    }
  }, [targetUserId]);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      
      
      const regionResponse = await axios.get(`https://test2.planfix-to-syrve.com/user-region/${targetUserId}`);
      const userRegion = regionResponse.data.region;
      setLanguage(userRegion);

      
      const response = await axios.get(`https://test2.planfix-to-syrve.com/users-admin`);
      const user = response.data.find(u => u.id === parseInt(targetUserId));
      
      if (user) {
        setUserData(user);
        setEmail(user.email);
        setUsername(user.username);
        setToken(user.token || '');
      } else {
        setError('Користувача не знайдено');
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching user data:', error);
      setError('Помилка при отриманні даних користувача');
      setLoading(false);
    }
  };

  const fetchContractorData = async () => {
    try {
      const response = await axios.get(`https://test2.planfix-to-syrve.com/user-contractor/${targetUserId}`);
      if (response.data && response.data.name) {
        setContractor(response.data);
      } else {
        setContractor(null);
      }
    } catch (error) {
      console.error('Error fetching contractor data:', error);
      setContractor(null);
    }
  };

  const handleSaveEmail = async () => {
    if (!isOwnProfile && !isAdmin) {
      setSaveError('Ви не можете редагувати цей профіль');
      return;
    }

    try {
      setSaveError('');
      setSaveSuccess('');
      
      if (email !== userData.email) {
        if (!currentPassword && isOwnProfile) {
          setSaveError('Введіть поточний пароль');
          return;
        }
        
        const payload = {
          email,
          ...(isOwnProfile ? { password: currentPassword } : {})
        };
        
        await axios.put(`https://test2.planfix-to-syrve.com/users/${targetUserId}`, payload);
        
        setSaveSuccess('Email успішно змінено');
        setCurrentPassword('');
        setIsEditingEmail(false);
        fetchUserData();
      } else {
        setSaveError('Email не змінився');
      }
    } catch (error) {
      setSaveError(error.response?.data?.message || 'Помилка при збереженні email');
    }
  };

  const handleSaveUsername = async () => {
    if (!isOwnProfile && !isAdmin) {
      setSaveError('Ви не можете редагувати цей профіль');
      return;
    }

    try {
      setSaveError('');
      setSaveSuccess('');
      
      if (username !== userData.username) {
        if (!currentPassword && isOwnProfile) {
          setSaveError('Введіть поточний пароль');
          return;
        }
        
        const payload = {
          username,
          ...(isOwnProfile ? { password: currentPassword } : {})
        };
        
        await axios.put(`https://test2.planfix-to-syrve.com/update-username/${targetUserId}`, payload);
        
        setSaveSuccess('Ім\'я користувача успішно змінено');
        setCurrentPassword('');
        setIsEditingUsername(false);
        fetchUserData();
      } else {
        setSaveError('Ім\'я не змінилося');
      }
    } catch (error) {
      setSaveError(error.response?.data?.message || 'Помилка при збереженні імені');
    }
  };

  const handleSaveToken = async () => {
    if (!isOwnProfile && !isAdmin) {
      setSaveError('Ви не можете редагувати цей профіль');
      return;
    }

    try {
      setSaveError('');
      setSaveSuccess('');
      
      if (token !== userData.token) {
        if (!currentPassword && isOwnProfile) {
          setSaveError('Введіть поточний пароль');
          return;
        }
        
        const payload = {
          token,
          ...(isOwnProfile ? { password: currentPassword } : {})
        };
        
        await axios.put(`https://test2.planfix-to-syrve.com/update-token/${targetUserId}`, payload);
        
        setSaveSuccess('Номер телефону успішно змінено');
        setCurrentPassword('');
        setIsEditingToken(false);
        fetchUserData();
      } else {
        setSaveError('Номер телефону не змінився');
      }
    } catch (error) {
      setSaveError(error.response?.data?.message || 'Помилка при збереженні номера телефону');
    }
  };

  const handleSavePassword = async () => {
    if (!isOwnProfile && !isAdmin) {
      setSaveError('Ви не можете змінювати пароль');
      return;
    }

    try {
      setSaveError('');
      setSaveSuccess('');
      
      if (!newPassword) {
        setSaveError('Введіть новий пароль');
        return;
      }

      if (!currentPassword && isOwnProfile) {
        setSaveError('Введіть поточний пароль');
        return;
      }
      
      const payload = {
        newPassword,
        ...(isOwnProfile ? { password: currentPassword } : {})
      };
      
      await axios.put(`https://test2.planfix-to-syrve.com/users/${targetUserId}`, payload);
      
      setSaveSuccess('Пароль успішно змінено');
      setCurrentPassword('');
      setNewPassword('');
      setIsEditingPassword(false);
      
    } catch (error) {
      setSaveError(error.response?.data?.message || 'Помилка при зміні пароля');
    }
  };

  const handleBack = () => {
    navigate('/users');
  };

  const cancelEdit = (type) => {
    switch(type) {
      case 'email':
        setIsEditingEmail(false);
        setEmail(userData.email);
        break;
      case 'username':
        setIsEditingUsername(false);
        setUsername(userData.username);
        break;
      case 'token':
        setIsEditingToken(false);
        setToken(userData.token || '');
        break;
      case 'password':
        setIsEditingPassword(false);
        break;
    }
    setCurrentPassword('');
    setNewPassword('');
    setSaveError('');
    setSaveSuccess('');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 15 }}>
        <Alert severity="error">{error}</Alert>
        <Button variant="contained" onClick={handleBack} sx={{ mt: 2 }}>
          Повернутись до списку
        </Button>
      </Container>
    );
  }

  if (!userData) {
    return (
      <Container maxWidth="md" sx={{ mt: 15 }}>
        <Alert severity="warning">Дані користувача не знайдено</Alert>
        <Button variant="contained" onClick={handleBack} sx={{ mt: 2 }}>
          Повернутись до списку
        </Button>
      </Container>
    );
  }

  return (
    <div className="profile-page" style={{ 
      padding: '20px', 
      maxWidth: '800px', 
      margin: '0 auto', 
      fontFamily: '-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Oxygen, Ubuntu, sans-serif', 
      backgroundColor: '#f5f5f5', 
      minHeight: '100vh' 
    }}>
      {}
      {!isOwnProfile && (
        <button 
          onClick={handleBack}
          style={{ 
            padding: '10px 20px',
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            width: 'auto',
            maxWidth: '100%'
          }}
        >
          <ArrowBackIcon sx={{ fontSize: '18px' }} /> Повернутись до списку
        </button>
      )}

      {}
      <div style={{ 
        backgroundColor: 'white', 
        borderRadius: '10px', 
        padding: '30px', 
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        maxWidth: '600px',
        margin: '0 auto',
        width: '100%',
        boxSizing: 'border-box'
      }}>
        <h2 style={{ margin: '0 0 25px 0', color: '#333', fontSize: '24px', textAlign: 'center', marginTop: "20px" }}>
          {isOwnProfile ? 'Мій профіль' : `Профіль користувача: ${userData.username}`}
        </h2>
        <Divider sx={{ mb: 3 }} />

        {}
        {!isEditingUsername ? (
          <div style={{ 
            display: 'flex', 
            flexDirection: 'row',
            flexWrap: 'wrap',
            justifyContent: 'space-between', 
            alignItems: 'center', 
            padding: '12px 0', 
            borderBottom: '1px solid #e9ecef',
            gap: '10px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', flex: '1', minWidth: '0', flexWrap: 'wrap' }}>
              <span style={{ fontWeight: '500', color: '#666', width: 'auto', minWidth: '140px', textAlign: 'left' }}>Ім'я користувача:</span>
              <span style={{ fontWeight: '600', color: '#333', flex: '1', textAlign: 'right', wordBreak: 'break-word' }}>{userData.username}</span>
            </div>
            {(isOwnProfile || isAdmin) && (
              <button
                onClick={() => setIsEditingUsername(true)}
                style={{
                  padding: '6px 12px',
                  backgroundColor: '#edec5b',
                  color: '#333',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  marginLeft: 'auto'
                }}
              >
                <EditIcon sx={{ fontSize: '14px' }} /> Змінити
              </button>
            )}
          </div>
        ) : (
          <div style={{ padding: '15px 0', borderBottom: '1px solid #e9ecef' }}>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', color: '#666', fontSize: '13px', fontWeight: '500' }}>Нове ім'я користувача</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #dee2e6',
                  borderRadius: '6px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            {isOwnProfile && (
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', color: '#666', fontSize: '13px', fontWeight: '500' }}>Поточний пароль</label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #dee2e6',
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            )}
            {saveError && <div style={{ color: '#dc3545', fontSize: '12px', marginBottom: '10px', padding: '8px', backgroundColor: '#f8d7da', borderRadius: '4px' }}>{saveError}</div>}
            {saveSuccess && <div style={{ color: '#155724', fontSize: '12px', marginBottom: '10px', padding: '8px', backgroundColor: '#d4edda', borderRadius: '4px' }}>{saveSuccess}</div>}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button
                onClick={() => cancelEdit('username')}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px'
                }}
              >
                <CloseIcon sx={{ fontSize: '14px', marginRight: '4px' }} /> Скасувати
              </button>
              <button
                onClick={handleSaveUsername}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#edec5b',
                  color: '#333',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '500'
                }}
              >
                <SaveIcon sx={{ fontSize: '14px', marginRight: '4px' }} /> Зберегти
              </button>
            </div>
          </div>
        )}

        {}
        {!isEditingEmail ? (
          <div style={{ 
            display: 'flex', 
            flexDirection: 'row',
            flexWrap: 'wrap',
            justifyContent: 'space-between', 
            alignItems: 'center', 
            padding: '12px 0', 
            borderBottom: '1px solid #e9ecef',
            gap: '10px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', flex: '1', minWidth: '0', flexWrap: 'wrap' }}>
              <span style={{ fontWeight: '500', color: '#666', width: 'auto', minWidth: '80px', textAlign: 'left' }}>Email:</span>
              <span style={{ fontWeight: '600', color: '#333', flex: '1', textAlign: 'right', wordBreak: 'break-word' }}>{userData.email}</span>
            </div>
            {(isOwnProfile || isAdmin) && (
              <button
                onClick={() => setIsEditingEmail(true)}
                style={{
                  padding: '6px 12px',
                  backgroundColor: '#edec5b',
                  color: '#333',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  marginLeft: 'auto'
                }}
              >
                <EditIcon sx={{ fontSize: '14px' }} /> Змінити
              </button>
            )}
          </div>
        ) : (
          <div style={{ padding: '15px 0', borderBottom: '1px solid #e9ecef' }}>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', color: '#666', fontSize: '13px', fontWeight: '500' }}>Новий email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #dee2e6',
                  borderRadius: '6px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            {isOwnProfile && (
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', color: '#666', fontSize: '13px', fontWeight: '500' }}>Поточний пароль</label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #dee2e6',
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            )}
            {saveError && <div style={{ color: '#dc3545', fontSize: '12px', marginBottom: '10px', padding: '8px', backgroundColor: '#f8d7da', borderRadius: '4px' }}>{saveError}</div>}
            {saveSuccess && <div style={{ color: '#155724', fontSize: '12px', marginBottom: '10px', padding: '8px', backgroundColor: '#d4edda', borderRadius: '4px' }}>{saveSuccess}</div>}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button
                onClick={() => cancelEdit('email')}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px'
                }}
              >
                <CloseIcon sx={{ fontSize: '14px', marginRight: '4px' }} /> Скасувати
              </button>
              <button
                onClick={handleSaveEmail}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#edec5b',
                  color: '#333',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '500'
                }}
              >
                <SaveIcon sx={{ fontSize: '14px', marginRight: '4px' }} /> Зберегти
              </button>
            </div>
          </div>
        )}

        {}
        {!isEditingToken ? (
          <div style={{ 
            display: 'flex', 
            flexDirection: 'row',
            flexWrap: 'wrap',
            justifyContent: 'space-between', 
            alignItems: 'center', 
            padding: '12px 0', 
            borderBottom: '1px solid #e9ecef',
            gap: '10px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', flex: '1', minWidth: '0', flexWrap: 'wrap' }}>
              <span style={{ fontWeight: '500', color: '#666', width: 'auto', minWidth: '140px', textAlign: 'left' }}>Номер телефону:</span>
              <span style={{ fontWeight: '600', color: '#333', flex: '1', textAlign: 'right', wordBreak: 'break-word' }}>{userData.token || 'Не вказано'}</span>
            </div>
            {(isOwnProfile || isAdmin) && (
              <button
                onClick={() => setIsEditingToken(true)}
                style={{
                  padding: '6px 12px',
                  backgroundColor: '#edec5b',
                  color: '#333',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  marginLeft: 'auto'
                }}
              >
                <EditIcon sx={{ fontSize: '14px' }} /> Змінити
              </button>
            )}
          </div>
        ) : (
          <div style={{ padding: '15px 0', borderBottom: '1px solid #e9ecef' }}>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', color: '#666', fontSize: '13px', fontWeight: '500' }}>Новий номер телефону</label>
              <input
                type="text"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #dee2e6',
                  borderRadius: '6px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            {isOwnProfile && (
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', color: '#666', fontSize: '13px', fontWeight: '500' }}>Поточний пароль</label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #dee2e6',
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            )}
            {saveError && <div style={{ color: '#dc3545', fontSize: '12px', marginBottom: '10px', padding: '8px', backgroundColor: '#f8d7da', borderRadius: '4px' }}>{saveError}</div>}
            {saveSuccess && <div style={{ color: '#155724', fontSize: '12px', marginBottom: '10px', padding: '8px', backgroundColor: '#d4edda', borderRadius: '4px' }}>{saveSuccess}</div>}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button
                onClick={() => cancelEdit('token')}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px'
                }}
              >
                <CloseIcon sx={{ fontSize: '14px', marginRight: '4px' }} /> Скасувати
              </button>
              <button
                onClick={handleSaveToken}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#edec5b',
                  color: '#333',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '500'
                }}
              >
                <SaveIcon sx={{ fontSize: '14px', marginRight: '4px' }} /> Зберегти
              </button>
            </div>
          </div>
        )}

        {}
        <div style={{ 
          display: 'flex', 
          flexDirection: 'row',
          flexWrap: 'wrap',
          justifyContent: 'space-between', 
          alignItems: 'center', 
          padding: '12px 0', 
          borderBottom: '1px solid #e9ecef',
          gap: '10px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', flex: '1', minWidth: '0', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: '500', color: '#666', width: 'auto', minWidth: '100px', textAlign: 'left' }}>Компанія:</span>
            <span style={{ fontWeight: '600', color: '#333', flex: '1', textAlign: 'right', wordBreak: 'break-word' }}>{contractor?.name || 'Не вказано'}</span>
          </div>
        </div>

        {}
        <div style={{ 
          display: 'flex', 
          flexDirection: 'row',
          flexWrap: 'wrap',
          justifyContent: 'space-between', 
          alignItems: 'center', 
          padding: '12px 0', 
          borderBottom: '1px solid #e9ecef',
          gap: '10px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', flex: '1', minWidth: '0', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: '500', color: '#666', width: 'auto', minWidth: '80px', textAlign: 'left' }}>Знижка:</span>
            <span style={{ fontWeight: '600', color: '#333', flex: '1', textAlign: 'right', wordBreak: 'break-word' }}>{userData.discount}%</span>
          </div>
        </div>

        {}
        {!isEditingPassword ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '20px 0 10px 0' }}>
            <button
              onClick={() => setIsEditingPassword(true)}
              style={{
                padding: '10px 24px',
                backgroundColor: '#edec5b',
                color: '#333',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                width: 'auto',
                maxWidth: '100%'
              }}
            >
              <LockIcon sx={{ fontSize: '16px' }} /> Змінити пароль
            </button>
          </div>
        ) : (
          <div style={{ padding: '15px 0', marginTop: '10px' }}>
            <h4 style={{ margin: '0 0 15px 0', color: '#333', fontSize: '16px' }}>Зміна пароля</h4>
            {isOwnProfile && (
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', color: '#666', fontSize: '13px', fontWeight: '500' }}>Поточний пароль</label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #dee2e6',
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            )}
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', color: '#666', fontSize: '13px', fontWeight: '500' }}>Новий пароль</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #dee2e6',
                  borderRadius: '6px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            {saveError && <div style={{ color: '#dc3545', fontSize: '12px', marginBottom: '10px', padding: '8px', backgroundColor: '#f8d7da', borderRadius: '4px' }}>{saveError}</div>}
            {saveSuccess && <div style={{ color: '#155724', fontSize: '12px', marginBottom: '10px', padding: '8px', backgroundColor: '#d4edda', borderRadius: '4px' }}>{saveSuccess}</div>}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button
                onClick={() => cancelEdit('password')}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px'
                }}
              >
                <CloseIcon sx={{ fontSize: '14px', marginRight: '4px' }} /> Скасувати
              </button>
              <button
                onClick={handleSavePassword}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#edec5b',
                  color: '#333',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '500'
                }}
              >
                <SaveIcon sx={{ fontSize: '14px', marginRight: '4px' }} /> Змінити пароль
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserProfile;
